import { useCallback, useRef } from 'react';

// Apple's scroll-deceleration projection (Designing Fluid Interfaces): where a
// release at `v` px/ms would come to rest.
const project = (v, rate = 0.998) => (v * rate) / (1 - rate);

/**
 * Drag a panel toward its closed edge to dismiss it.
 *
 * Returns a pointerdown handler for the grab area (a header, a handle). The
 * panel follows the finger 1:1 from where it was grabbed, resists past its open
 * position, and on release closes if the *projected* resting point — position
 * plus momentum — is past a third of its size. The panel's own CSS transition
 * carries on from where the finger left it, so a flick and a slow drag both
 * land without a seam.
 *
 * axis 'y' closes downward, 'x' closes to the right.
 */
const useDragDismiss = (panelRef, onClose, { axis = 'y', enabled = true } = {}) => {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    return useCallback((e) => {
        const panel = panelRef.current;
        if (!enabled || !panel || e.button > 0 || e.target.closest('button, a, input, select, textarea, [role="button"]')) return;

        const start = axis === 'y' ? e.clientY : e.clientX;
        const size = axis === 'y' ? panel.offsetHeight : panel.offsetWidth;
        let samples = [[e.timeStamp, 0]];
        let offset = 0;
        let dragging = false;

        const move = (ev) => {
            const raw = (axis === 'y' ? ev.clientY : ev.clientX) - start;
            // ~10px of hysteresis before the gesture commits
            if (!dragging) {
                if (Math.abs(raw) < 10) return;
                dragging = true;
                panel.setPointerCapture?.(e.pointerId);
                panel.style.transition = 'none';
            }
            // past the open edge: rubber-band instead of a hard stop
            offset = raw >= 0 ? raw : -(Math.sqrt(-raw) * 3);
            panel.style.transform = axis === 'y' ? `translateY(${offset}px)` : `translateX(${offset}px)`;
            samples.push([ev.timeStamp, offset]);
            samples = samples.filter(([t]) => ev.timeStamp - t < 100);
        };

        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            window.removeEventListener('pointercancel', up);
            if (!dragging) return;
            const [t0, p0] = samples[0];
            const [t1, p1] = samples[samples.length - 1];
            const velocity = t1 > t0 ? (p1 - p0) / (t1 - t0) : 0;
            panel.style.transition = '';
            if (offset + project(velocity) > size / 3) {
                // head straight for the closed edge; the class takes over once it lands
                panel.style.transform = axis === 'y' ? 'translateY(100%)' : 'translateX(100%)';
                panel.addEventListener('transitionend', () => { panel.style.transform = ''; }, { once: true });
                closeRef.current?.();
            } else {
                panel.style.transform = '';
            }
        };

        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        window.addEventListener('pointercancel', up);
    }, [panelRef, axis, enabled]);
};

export default useDragDismiss;
